import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaSave, FaSearch, FaSpinner, FaSync } from 'react-icons/fa';
import { useToast } from '../../contexts/ToastContext';
import API from '../../utils/api';
import './PrecioDelivery.css';
import './Configuracion.css';

const PLATAFORMAS = [
  { key: 'uber',    label: 'Uber Eats', color: '#111827', accent: '#f3f4f6' },
  { key: 'rappi',   label: 'Rappi',     color: '#FF441F', accent: '#fff5f3' },
  { key: 'delibot', label: 'Delibot',   color: '#4f46e5', accent: '#eef2ff' },
];

const round2 = (n) => Math.round(n * 100) / 100;
const calcPrecio = (base, pct) => round2(base * (1 + pct / 100));
const calcPct    = (base, precio) => base > 0 ? round2(((precio - base) / base) * 100) : 0;

// ── Celda de precio editable ───────────────────────────────────────────────────
const PrecioInput = ({ value, precioBase, onChange }) => {
  const [local, setLocal] = useState(String(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setLocal(String(value));
  }, [value]);

  const pct = calcPct(precioBase, Number(local) || 0);

  return (
    <div className="pd-precio-cell">
      <div className="pd-precio-row">
        <span className="pd-precio-symbol">$</span>
        <input
          className="pd-precio-input"
          type="number" min="0" step="0.01"
          value={local}
          onFocus={e => {
            focused.current = true;
            e.target.style.borderColor = '#6366f1';
          }}
          onChange={e => setLocal(e.target.value)}
          onBlur={e => {
            focused.current = false;
            e.target.style.borderColor = '#e5e7eb';
            const v = Math.max(0, Number(local) || 0);
            setLocal(String(v));
            onChange(v);
          }}
        />
      </div>
      <span className="pd-precio-pct" style={{
        color: pct > 0 ? '#16a34a' : pct < 0 ? '#dc2626' : '#9ca3af',
      }}>
        {pct > 0 ? '+' : ''}{pct}%
      </span>
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const PrecioDelivery = () => {
  const toast = useToast();

  const [productos, setProductos]   = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [edits, setEdits]           = useState({});
  const [busqueda, setBusqueda]     = useState('');
  const [catFiltro, setCatFiltro]   = useState('');
  const [pctGlobal, setPctGlobal]   = useState({ uber: 30, rappi: 30, delibot: 30 });

  // ── Carga ────────────────────────────────────────────────────────────────────

  const cargarProductos = async () => {
    setCargando(true);
    try {
      const prods = await API.impresoras.getPlatillosDelivery();
      setProductos(prods);
      const init = {};
      prods.forEach(p => {
        init[p.idPlatillo] = {
          uber:    p.precioUber,
          rappi:   p.precioRappi,
          delibot: p.precioDelibot,
        };
      });
      setEdits(init);
    } catch {
      toast.error('Error al cargar los productos');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarProductos(); }, []);

  // ── Aplicar % global ─────────────────────────────────────────────────────────

  const aplicarGlobal = (platKey) => {
    const pct = pctGlobal[platKey];
    setEdits(prev => {
      const next = { ...prev };
      productos.forEach(p => {
        next[p.idPlatillo] = {
          ...(next[p.idPlatillo] || {}),
          [platKey]: calcPrecio(p.precio, pct),
        };
      });
      return next;
    });
    toast.success(`${pct}% aplicado a todos en ${PLATAFORMAS.find(x => x.key === platKey)?.label}`);
  };

  const aplicarTodos = () => {
    setEdits(prev => {
      const next = { ...prev };
      productos.forEach(p => {
        next[p.idPlatillo] = {
          uber:    calcPrecio(p.precio, pctGlobal.uber),
          rappi:   calcPrecio(p.precio, pctGlobal.rappi),
          delibot: calcPrecio(p.precio, pctGlobal.delibot),
        };
      });
      return next;
    });
    toast.success('Porcentajes aplicados a todos los productos');
  };

  // ── Editar precio individual ──────────────────────────────────────────────────

  const setPrecio = (idPlatillo, platKey, valor) => {
    setEdits(prev => ({
      ...prev,
      [idPlatillo]: { ...(prev[idPlatillo] || {}), [platKey]: valor },
    }));
  };

  // ── Filtros ───────────────────────────────────────────────────────────────────

  const categorias = useMemo(
    () => [...new Set(productos.map(p => p.nombreCategoria))].sort(),
    [productos]
  );

  const filtrados = useMemo(() =>
    productos.filter(p => {
      const ok1 = !busqueda  || p.nombre.toLowerCase().includes(busqueda.toLowerCase());
      const ok2 = !catFiltro || p.nombreCategoria === catFiltro;
      return ok1 && ok2;
    }),
    [productos, busqueda, catFiltro]
  );

  // ── Guardar ───────────────────────────────────────────────────────────────────

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const platillos = productos.map(p => {
        const e = edits[p.idPlatillo] || {};
        const uber    = e.uber    ?? calcPrecio(p.precio, 30);
        const rappi   = e.rappi   ?? calcPrecio(p.precio, 30);
        const delibot = e.delibot ?? calcPrecio(p.precio, 30);
        return {
          idPlatillo: p.idPlatillo,
          uber:    { pct: calcPct(p.precio, uber),    precio: uber },
          rappi:   { pct: calcPct(p.precio, rappi),   precio: rappi },
          delibot: { pct: calcPct(p.precio, delibot), precio: delibot },
        };
      });

      // Guardar en localStorage (siempre disponible para los pedidos)
      const mapaLocal = {};
      platillos.forEach(({ idPlatillo, ...rest }) => { mapaLocal[idPlatillo] = rest; });
      localStorage.setItem('delivery_precios_platillos', JSON.stringify(mapaLocal));

      // Guardar en BD
      try {
        await API.impresoras.actualizarPreciosDelivery(platillos);
        toast.success('Precios guardados correctamente');
      } catch (apiErr) {
        toast.error('No se pudo guardar en BD (ejecuta el script SQL). Los precios ya están activos en esta sesión.');
      }
    } catch (err) {
      toast.error(err.message || 'Error al guardar los precios');
    } finally {
      setGuardando(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="config-content-form pd-root" style={{ padding: 0 }}>

      {/* ── Panel global ───────────────────────────────────────────────────────── */}
      <div className="pd-global-panel">
        <p className="pd-global-label">Aplicar porcentaje a todos los productos</p>
        <div className="pd-global-cards">
          {PLATAFORMAS.map(({ key, label, color, accent }) => (
            <div
              key={key}
              className="pd-plat-card"
              style={{ background: accent, border: `1px solid ${color}30` }}
            >
              <span className="pd-plat-card-label" style={{ color }}>{label}</span>
              <div className="pd-plat-card-row">
                <input
                  className="pd-plat-pct-input"
                  type="number" min="0" max="999" step="1"
                  value={pctGlobal[key]}
                  onChange={e => setPctGlobal(prev => ({
                    ...prev,
                    [key]: Math.max(0, Number(e.target.value) || 0),
                  }))}
                  style={{ border: `1px solid ${color}40`, color }}
                />
                <span className="pd-plat-pct-symbol" style={{ color }}>%</span>
                <button
                  className="pd-plat-btn"
                  onClick={() => aplicarGlobal(key)}
                  disabled={cargando || productos.length === 0}
                  style={{
                    background: color,
                    opacity: productos.length === 0 ? 0.4 : 1,
                  }}
                >
                  Aplicar
                </button>
              </div>
            </div>
          ))}

          <button
            className="pd-apply-all-btn"
            onClick={aplicarTodos}
            disabled={cargando || productos.length === 0}
            style={{ opacity: productos.length === 0 ? 0.4 : 1 }}
          >
            Aplicar todo
          </button>
        </div>
      </div>

      {/* ── Barra filtros ───────────────────────────────────────────────────────── */}
      <div className="pd-filter-bar">
        <div className="pd-search-wrap">
          <FaSearch className="pd-search-icon" />
          <input
            className="pd-search-input"
            type="text" placeholder="Buscar producto..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        <select
          className="pd-cat-select"
          value={catFiltro} onChange={e => setCatFiltro(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <span className="pd-count">{filtrados.length} de {productos.length} productos</span>

        <button
          className="pd-reload-btn"
          onClick={cargarProductos} disabled={cargando} title="Recargar"
        >
          {cargando ? <FaSpinner className="spin" /> : <FaSync />}
        </button>
      </div>

      {/* ── Tabla ───────────────────────────────────────────────────────────────── */}
      {cargando ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
          <FaSpinner className="spin" style={{ fontSize: '20px' }} />
          <p style={{ marginTop: '8px', fontSize: '13px' }}>Cargando productos...</p>
        </div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th className="pd-th pd-th-producto">Producto</th>
                <th className="pd-th pd-th-base">Precio base</th>
                {PLATAFORMAS.map(({ key, label, color, accent }) => (
                  <th
                    key={key}
                    className="pd-th pd-th-plat"
                    style={{
                      color,
                      background: accent,
                      borderLeft: `2px solid ${color}25`,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr>
                  <td
                    colSpan={2 + PLATAFORMAS.length}
                    style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}
                  >
                    No se encontraron productos
                  </td>
                </tr>
              ) : (
                filtrados.map((p, idx) => (
                  <tr
                    key={p.idPlatillo}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa',
                    }}
                  >
                    <td className="pd-td-producto">
                      <div className="pd-td-nombre">{p.nombre}</div>
                      <div className="pd-td-cat">{p.nombreCategoria}</div>
                    </td>

                    <td className="pd-td-base">${p.precio?.toFixed(2)}</td>

                    {PLATAFORMAS.map(({ key, color, accent }) => {
                      const precio = edits[p.idPlatillo]?.[key] ?? calcPrecio(p.precio, 30);
                      return (
                        <td
                          key={key}
                          className="pd-td-plat"
                          style={{
                            background: idx % 2 === 0 ? accent : `${accent}cc`,
                            borderLeft: `2px solid ${color}15`,
                          }}
                        >
                          <PrecioInput
                            value={precio}
                            precioBase={p.precio}
                            onChange={v => setPrecio(p.idPlatillo, key, v)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────────────── */}
      <div className="pd-footer">
        <button
          className="config-btn config-btn-primary"
          onClick={handleGuardar}
          disabled={guardando || cargando}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {guardando
            ? <><FaSpinner className="spin" /> Guardando...</>
            : <><FaSave /> Guardar</>}
        </button>
      </div>
    </div>
  );
};

export default PrecioDelivery;
